/**
 * Read, write, and delete {@link https://developer.mozilla.org/en-US/docs/Glossary/Cookie | cookies}
 * ({@link https://developer.mozilla.org/en-US/docs/Glossary/CRUD | CRUD}). Also listens when the
 * Usercentrics cookie banner (CMP, consent management platform) changes consent.
 *
 * Returning visitors who already allowed Maps or YouTube skip the overlay
 * (consent gate): ConsentGateBoot.html (in the page head) reads the cookies
 * and sets `data-consent-maps` / `data-consent-youtube` on `<html>` before
 * first paint. This helper updates those same attributes when consent is
 * given or revoked, so the overlays stay correct without reloading the page.
 *
 * New cookies use `document.cookie` — {@link https://developer.mozilla.org/en-US/docs/Glossary/Baseline/Compatibility | Baseline widely available} —
 * with a lifetime in seconds (`Max-Age`), `path=/` (whole site),
 * `SameSite=Lax` (not sent on most cross-site requests), and `Secure` in a
 * {@link https://developer.mozilla.org/en-US/docs/Glossary/Secure_context | secure context}
 * ({@link https://developer.mozilla.org/en-US/docs/Glossary/HTTPS | HTTPS} and localhost;
 * not sent over plain HTTP).
 */

const COOKIE_PATH = '/';
const SECONDS_PER_DAY = 24 * 60 * 60;

/**
 * The `name=value` snippet from `document.cookie`, or `null` if missing.
 *
 * @param {string} name
 * @returns {string|null}
 */
function readCookiePair(name) {
    const prefix = `${name}=`;

    return document.cookie.split(';').map((part) => part.trim()).find((row) => row.startsWith(prefix)) ?? null;
}

/**
 * The part after the cookie value: path, lifetime, SameSite, and Secure in a
 * {@link https://developer.mozilla.org/en-US/docs/Glossary/Secure_context | secure context}
 * ({@link https://developer.mozilla.org/en-US/docs/Glossary/HTTPS | HTTPS} and localhost).
 *
 * `legacy: true` leaves off SameSite and Secure. Needed when deleting older
 * cookies that were stored without those flags — the browser only removes a
 * cookie if this part matches how it was originally set.
 *
 * @param {number} maxAgeSeconds
 * @param {{legacy?: boolean}} [options]
 * @returns {string}
 */
function cookieAttributes(maxAgeSeconds, options = {}) {
    const parts = [`path=${COOKIE_PATH}`, `Max-Age=${maxAgeSeconds}`];

    if (!options.legacy) {
        parts.push('SameSite=Lax');
        if (typeof window !== 'undefined' && window.isSecureContext) {
            parts.push('Secure');
        }
    }

    return parts.join('; ');
}

/**
 * Read, write, and delete cookies ({@link https://developer.mozilla.org/en-US/docs/Glossary/CRUD | CRUD}),
 * and keep map/video consent overlays (gates) in sync.
 */
class CookieHelper {
    /**
     * Value of the named cookie, or `null` if it does not exist.
     * Use this when you need the contents; use `getCookie` only to test existence.
     * Percent-encoded characters (from `encodeURIComponent`;
     * {@link https://developer.mozilla.org/en-US/docs/Glossary/Percent-encoding | percent encoding})
     * are decoded.
     *
     * @param {string} name Cookie name
     * @returns {string|null}
     */
    static getCookieValue(name) {
        const pair = readCookiePair(name);
        if (pair == null) {
            return null;
        }

        const raw = pair.slice(name.length + 1);
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }

    /**
     * Whether a cookie with the given name exists (`true`/`false`).
     * Does not return the value — use `getCookieValue` for that.
     *
     * @param {string} name Cookie name
     * @returns {boolean}
     */
    static getCookie(name) {
        return readCookiePair(name) != null;
    }

    /**
     * Delete the named cookie by setting its lifetime to zero.
     *
     * Writes twice: once with current flags, and once without SameSite/Secure
     * so older Google Maps / YouTube consent cookies are removed too.
     *
     * @param {string} name Cookie name
     */
    static clearCookie(name) {
        document.cookie = `${name}=; ${cookieAttributes(0, { legacy: true })}`;
        document.cookie = `${name}=; ${cookieAttributes(0)}`;
        CookieHelper.syncConsentGateFlags(name);
    }

    /**
     * Set a cookie with an expiration in days (`Max-Age` in seconds).
     * Always `path=/` and `SameSite=Lax`; `Secure` is added in a secure context
     * (HTTPS and localhost). Values are percent-encoded.
     *
     * @param {string} name Cookie name
     * @param {string|boolean|number} value Cookie value
     * @param {number} [expirationDays=31] Lifetime in days
     */
    static setCookie(name, value, expirationDays = 31) {
        const maxAge = Math.round(Number(expirationDays) * SECONDS_PER_DAY);
        const encoded = encodeURIComponent(String(value));

        document.cookie = `${name}=${encoded}; ${cookieAttributes(maxAge)}`;
        CookieHelper.syncConsentGateFlags(name);
    }

    /**
     * After a `consent-type-*` cookie is set or deleted, set or remove
     * `data-consent-maps` and `data-consent-youtube` on `<html>`. CSS uses
     * those attributes to hide the map and video consent overlays (gates).
     * Other cookie names are ignored.
     *
     * @param {string} name Cookie that just changed
     */
    static syncConsentGateFlags(name) {
        if (!name.startsWith('consent-type-') || typeof document === 'undefined') {
            return;
        }

        const marketing = CookieHelper.getCookieValue('consent-type-marketing') === 'true';
        const maps = Boolean(window.mapsConsentGiven)
            || marketing
            || CookieHelper.getCookieValue('consent-type-google-maps') === 'true';
        const youtube = Boolean(window.youtubeConsentGiven)
            || marketing
            || CookieHelper.getCookieValue('consent-type-youtube') === 'true';

        document.documentElement.toggleAttribute('data-consent-maps', maps);
        document.documentElement.toggleAttribute('data-consent-youtube', youtube);
    }

    /**
     * Listen for Usercentrics consent updates (`UC_CONSENT`).
     *
     * The first event is the cookie banner (CMP) starting up and is ignored. Later events
     * check whether YouTube or Google Maps were accepted and fire matching
     * window events so the Video and Map components can react.
     *
     * @param {string[]} [customEvents=[]] Event names such as `consent-type-youtube`
     */
    static addMarketingCookieListener(customEvents = []) {
        CookieHelper.consentInitialized = false;
        window.addEventListener('UC_CONSENT', (e) => {
            if (CookieHelper.consentInitialized === false) {
                CookieHelper.consentInitialized = true;
                return;
            }
            if (e.detail) {
                for (const event of customEvents) {
                    if (event === 'consent-type-youtube') {
                        const services = e.detail?.services || {};

                        const youtubeConsentGiven = Object.values(services).some(
                            (service) => service.name === 'YouTube Video' && service.consent?.given === true,
                        );

                        window.youtubeConsentGiven = youtubeConsentGiven;

                        let customEvent = new CustomEvent('consent-withdraw-youtube');
                        if (youtubeConsentGiven) {
                            customEvent = new CustomEvent('consent-type-youtube');
                        }
                        window.dispatchEvent(customEvent);
                    }
                    if (event === 'consent-type-google-maps') {
                        const services = e.detail?.services || {};

                        const mapsConsentGiven = Object.values(services).some(
                            (service) => service.name === 'Google Maps' && service.consent?.given === true,
                        );

                        window.mapsConsentGiven = mapsConsentGiven;

                        let customEvent = new CustomEvent('consent-withdraw-maps');
                        if (mapsConsentGiven) {
                            customEvent = new CustomEvent('consent-type-google-maps');
                        }
                        window.dispatchEvent(customEvent);
                    }
                }
            }
        }, false);
    }
}

export default CookieHelper;
